import apiClient from '@/services/api'
import * as Sentry from '@sentry/vue'
import moment from 'moment-timezone'
import { getSupportScope } from '@/services/support-diagnostics'

const getConversation = state => ({
  scope: getSupportScope(),
  version: state.conversationVersion,
  id: state.conversation?.id ?? null
})
const isCurrentConversation = (state, conversation) =>
  conversation.scope === getSupportScope() &&
  conversation.version === state.conversationVersion &&
  (!conversation.id || conversation.id === state.conversation?.id)

const validateResponse = data => {
  if (data.error_code && data.error_code !== '0') {
    throw new Error(`${data.msg}`)
  }

  if (!data.conversation) {
    throw new Error('The support conversation could not be loaded.')
  }

  return data.conversation
}

const getAttachmentErrorMessage = error => {
  const response = error?.response?.data ?? {}
  const fieldError = response.image ?? response.file_id

  return fieldError?.message ?? response.msg ?? error?.message ?? 'The image could not be uploaded. Try again.'
}

const getAttachmentUrlKey = (conversationId, fileId) => `${conversationId}:${fileId}`

const hasStaleConversationError = error => {
  if (error?.response?.status !== 400) return false

  const errorValue = error.response.data?.conversation_id
  const messages = Array.isArray(errorValue) ? errorValue : [errorValue]
  return messages.some(message => String(message).includes('Conversation is no longer current'))
}

export default {
  async getMessages({ commit, rootState, state }) {
    const conversation = getConversation(state)
    const currentConversation = state.conversation
    const { data } = await apiClient.get('/api/assistant_chat/history', {
      params: { associate_id: rootState.applicationState.associate.id }
    })

    const snapshot = validateResponse(data)
    if (!isCurrentConversation(state, conversation) || state.conversation !== currentConversation) return false
    commit('SET_CONVERSATION', snapshot)
    return true
  },

  async postMessage(
    { commit, rootState, state },
    { content, attachment = null, imageAnalysisConsent = false, reusePreviousAttachment = false }
  ) {
    const conversation = getConversation(state)
    const eventId = state.eventId
    const currentConversation = state.conversation
    const newMessage = {
      id: `temporary-${conversation.version}-${Date.now()}`,
      content,
      created_at: moment().unix(),
      role: 'user',
      type: 'message',
      temporary: true,
      ...(attachment ? { attachment } : {})
    }

    try {
      commit('SET_CONVERSATION', {
        ...currentConversation,
        messages: [...(currentConversation?.messages ?? []), newMessage].sort((a, b) => a.created_at - b.created_at)
      })

      const { data } = await apiClient.post('/api/assistant_chat/send', {
        content,
        associate_id: rootState.applicationState.associate.id,
        ...(currentConversation?.id ? { conversation_id: currentConversation.id } : {}),
        ...(eventId ? { event_id: eventId } : {}),
        ...(attachment?.file_id ? { file_id: attachment.file_id } : {}),
        ...(reusePreviousAttachment ? { reuse_previous_attachment: true } : {}),
        ...(imageAnalysisConsent ? { image_analysis_consent: true } : {})
      })

      const snapshot = validateResponse(data)

      if (!isCurrentConversation(state, conversation)) return false
      commit('SET_CONVERSATION', snapshot)
      return true
    } catch (error) {
      if (isCurrentConversation(state, conversation)) {
        commit('SET_CONVERSATION', currentConversation)
      }
      if (attachment) throw new Error(getAttachmentErrorMessage(error))
      throw error
    }
  },

  async uploadSupportImage({ rootState, state }, { image }) {
    const formData = new FormData()
    formData.append('associate_id', rootState.applicationState.associate.id)
    formData.append('conversation_id', state.conversation.id)
    formData.append('contains_no_prohibited_data', 'true')
    formData.append('image', image)

    try {
      const { data } = await apiClient.post('/api/assistant_chat/attachments', formData, {
        skipSentryHttpError: true
      })

      if (!data.attachment?.file_id) {
        throw new Error(getAttachmentErrorMessage({ response: { data } }))
      }

      return data.attachment
    } catch (error) {
      throw new Error(getAttachmentErrorMessage(error))
    }
  },

  async getSupportAttachmentUrl({ commit, rootState, state }, { fileId, conversationId }) {
    const scope = getSupportScope()
    const key = getAttachmentUrlKey(conversationId, fileId)
    if (state.attachmentUrls[key]) return state.attachmentUrls[key]

    const { data } = await apiClient.get(`/api/assistant_chat/attachments/${fileId}`, {
      params: {
        associate_id: rootState.applicationState.associate.id,
        conversation_id: conversationId
      },
      skipSentryHttpError: true
    })

    if (!data.url) throw new Error('The image could not be loaded.')
    if (scope !== getSupportScope() || state.conversation?.id !== conversationId) {
      throw new Error('The image could not be loaded.')
    }

    commit('SET_ATTACHMENT_URL', { key, url: data.url })
    return data.url
  },

  async deleteSupportAttachment({ commit, rootState }, { fileId, conversationId }) {
    const key = getAttachmentUrlKey(conversationId, fileId)
    await apiClient.delete(`/api/assistant_chat/attachments/${fileId}`, {
      data: {
        associate_id: rootState.applicationState.associate.id,
        conversation_id: conversationId
      },
      skipSentryHttpError: true
    })
    commit('DELETE_ATTACHMENT_URL', key)
  },

  async postStartNewTopic({ commit, dispatch, rootState, state }) {
    const conversation = getConversation(state)
    const conversationId = state.conversation?.id
    const currentConversation = state.conversation

    try {
      const { data } = await apiClient.post('/api/assistant_chat/start_new_topic', {
        associate_id: rootState.applicationState.associate.id,
        conversation_id: conversationId
      })

      const snapshot = validateResponse(data)
      if (isCurrentConversation(state, conversation) && state.conversation === currentConversation) {
        commit('START_NEW_TOPIC', snapshot)
        return true
      }

      const refreshed = await dispatch('getMessages')
      if (refreshed && state.conversation?.id !== conversationId) {
        commit('START_NEW_TOPIC', state.conversation)
      }
      return refreshed
    } catch (error) {
      if (!hasStaleConversationError(error) && error?.response) throw error

      const refreshed = await dispatch('getMessages')
      if (refreshed && state.conversation?.id !== conversationId) {
        commit('START_NEW_TOPIC', state.conversation)
        return true
      }
      if (hasStaleConversationError(error)) return false
      throw error
    }
  },

  async dismissTopicSuggestion({ commit, rootState, state }) {
    const conversation = getConversation(state)
    const currentConversation = state.conversation
    const { data } = await apiClient.post('/api/assistant_chat/topic_suggestion/dismiss', {
      associate_id: rootState.applicationState.associate.id,
      conversation_id: state.conversation.id
    })

    const snapshot = validateResponse(data)

    if (!isCurrentConversation(state, conversation) || state.conversation !== currentConversation) return false
    commit('SET_CONVERSATION', snapshot)
    return true
  },

  async fetchResponsibleContact({ rootState }, { contactId }) {
    const { data } = await apiClient.get(
      `/api/associate/${rootState.applicationState.associate.id}/responsible_contacts`
    )

    if (data.error_code && data.error_code !== '0') {
      throw new Error(`${data.msg}`)
    }

    return Object.values(data.contacts ?? {})
      .flat()
      .find(contact => String(contact.id) === String(contactId))
  },

  async postFeedback({ rootState, state }, { content, attachments = [] }) {
    const conversation = getConversation(state)
    const userFeedback = {
      name: `${rootState.applicationState.associate.first_name} ${rootState.applicationState.associate.last_name}`,
      email: rootState.applicationState.associate.email,
      message: content,
      ...(state.eventId ? { associatedEventId: state.eventId } : {})
    }

    await Sentry.sendFeedback(userFeedback, {
      includeReplay: false,
      attachments
    })
    return isCurrentConversation(state, conversation)
  }
}
